import * as React from 'react';
import * as Images from 'pos/Images';
import { z } from 'zod';

import { Res, WorkingPeriodStatusDTO } from 'core/api';
import { Result } from 'core/infrastructure';
import { Content, Header, PosScreen } from 'pos/ui';
import { Modal } from 'core/ui-next/modal';
import { FormRef, useControlsFor } from 'core/ui-next/controls';
import { CardPanel, BasicButton } from 'core/ui-next';
import { Box, VStack, Text } from '@chakra-ui/react';
import * as Clocking from './Clocking';
import { DateUtils } from 'core/utils';
import { zBusinessDay, zDate } from 'core/infrastructure-next';
import * as UI from 'core/ui';

const styleSchema = z.object({
	BackColor: z.string(),
	ImageId: z.string(),
	Text: z.string(),
});

const clockingUserSchema = z.object({
	CanClockingWithCardNumber: z.boolean(),
	CanClockingWithPassword: z.boolean(),
	CanModifyClockInDateTime: z.boolean(),
	CanModifyClockOutDateTime: z.boolean(),
	Id: z.number(),
	Name: z.string(),
	RequiresPassword: z.boolean(),
	ShowInClockings: z.boolean(),
	Style: styleSchema,
});

const workingPeriodSchema = z.object({
	BusinessDay: zBusinessDay(),
	CanChangeBusinessDay: z.boolean(),
	ClockIn: zDate().nullable(),
	ClockOut: zDate().nullable(),
	User: clockingUserSchema,
});

type Props = { workingPeriod: z.infer<typeof workingPeriodSchema> } & Modal.Actions<WorkingPeriodStatusDTO>;
type State = z.infer<typeof workingPeriodSchema>;

function EditClocking(props: Props) {

	const { workingPeriod, cancel, close } = props;
	const isClocking = workingPeriod.ClockIn === null;

	const { Form, DateInput } = useControlsFor<State>();

	const formRef = React.useRef<FormRef<State>>(null);

	const onAccept = React.useCallback(async () => {

		const result = await formRef.current!.getValue();
		if (!result.ok)
			return;

		close(result.value);

	}, [close]);


	const validate = React.useCallback(async (data: WorkingPeriodStatusDTO) =>
		isClocking ? Clocking.validateClockIn(data) : Clocking.validateClockOut(data), [isClocking]);


	const disableAcceptButton = isClocking
		? !Clocking.canEditBusinessDay(workingPeriod) && !Clocking.canEditClockInDate(workingPeriod)
		: !Clocking.canEditClockOutDate(workingPeriod);

	return <PosScreen screenId='EDIT_CLOCKING' size={'small'}>

		<Header title={Res.Web.CLOCKING_VIEW_EDIT()} image={Images.AgoraLogo} onClose={cancel} />

		<Content>
			<Box paddingTop={'14'} paddingX='6' paddingBottom='6' height={'full'}>
				<Form initialValue={{
					...workingPeriod,
					ClockIn: workingPeriod.ClockIn ?? new Date(),
					ClockOut: isClocking ? null : workingPeriod.ClockOut ?? new Date()
				}} schema={workingPeriodSchema} ref={formRef} validate={validate}>
					<VStack gap='6' w='full'>
						{
							isClocking
								? <>
									<DateInput label={Res.Web.CLOCKING_VIEW_BUSINESS_DAY_LBL()} type='date' linkTo='BusinessDay' disabled={!Clocking.canEditBusinessDay(workingPeriod)} />
									<DateInput label={Res.Web.CLOCKING_VIEW_CLOCK_IN_DATE_LBL()} type='datetime-local' linkTo='ClockIn' disabled={!Clocking.canEditClockInDate(workingPeriod)} />
								</>
								: <>
									<CardPanel w='80' h='32'>
										<Text fontSize='xs' fontWeight='semibold'>{Res.Web.CLOCKING_VIEW_BUSINESS_DAY_LBL()}</Text>
										<Text fontSize='xs'>{DateUtils.formatDate(workingPeriod.BusinessDay, 'dd/MM/yyyy')}</Text>
										<Text fontSize='xs' fontWeight='semibold'>{Res.Web.CLOCKING_VIEW_CLOCK_IN_DATE_LBL()}</Text>
										<Text fontSize='xs'>{DateUtils.formatDate(workingPeriod.ClockIn!, 'dd/MM/yyyy HH:mm:ss')}</Text>
									</CardPanel>
									<DateInput label={Res.Web.CLOCKING_VIEW_CLOCK_OUT_DATE_LBL()} type='datetime-local' linkTo='ClockOut' disabled={!Clocking.canEditClockOutDate(workingPeriod)} onKeyDown={UI.whenEnterKey(onAccept)} />
								</>
						}

						<BasicButton type='success' text={Res.Common.OK_BTN()} onClick={onAccept} disabled={disableAcceptButton} />
					</VStack>
				</Form>
			</Box>
		</Content>

	</PosScreen>;
}

export default async function editClocking(workingPeriod: WorkingPeriodStatusDTO): Promise<Result<WorkingPeriodStatusDTO>> {
	return await Modal.show(EditClocking, { workingPeriod });
}